import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/store/store";
import type { ApiError, LoadingStatus } from "@/shared/types";
import { mockFetch } from "@/widgets/common/mockFetch";
import { API_GRANULARITY, mapResponseToPoints } from "./logic";
import { generatePrintOverviewResponse } from "./mock";
import type {
  ApiGranularity,
  OverviewGranularity,
  PrintOverviewPoint,
  PrintOverviewResponseRow,
} from "./types";

/**
 * Widget state for the print-overview page: a CACHE OF POINTS PER API
 * GRANULARITY (day / month / year) instead of a single points array.
 *
 * Why hand-written (not the `createDataWidget` factory): the page has TWO
 * charts sharing ONE data source, each with its OWN drill level. A flat
 * points array is wiped by every refetch — drilling chart A erased the
 * level chart B was displaying (its `buildDisplayPoints` filter then came
 * up empty). Keying the cache by granularity makes each level's fetch
 * replace ONLY that level; the other chart keeps rendering its own.
 *
 * `week` is not an API key: it fetches day rows (`API_GRANULARITY`) and is
 * re-bucketed client-side in `logic.ts` — so the cache keys are the THREE
 * API granularities. Swapping in CubeJS replaces the mock body only.
 */

/** Granularities the API serves (and the cache keys). */
const API_KEYS: ApiGranularity[] = ["day", "month", "year"];

const fetchRows = mockFetch<PrintOverviewResponseRow, OverviewGranularity>(
  (granularity) => generatePrintOverviewResponse(granularity ?? "month"),
);

/** The page granularity in, API granularity mapped + mapped points out. */
export const fetchPrintOverview = createAsyncThunk<
  PrintOverviewPoint[],
  OverviewGranularity,
  { state: RootState }
>("printOverview/fetch", async (granularity, { signal }) => {
  const rows = await fetchRows(granularity, signal);
  // The CACHE KEY rides in `meta.arg` (extraReducers) — the payload itself
  // stays plain `PrintOverviewPoint[]`, matching the shell's fetch contract.
  return mapResponseToPoints(rows, API_GRANULARITY[granularity]);
});

interface PrintOverviewState {
  /** Cached points per API granularity; `null` = never fetched. */
  pointsByKey: Record<ApiGranularity, PrintOverviewPoint[] | null>;
  statusByKey: Record<ApiGranularity, LoadingStatus>;
  errorByKey: Record<ApiGranularity, ApiError | null>;
}

const initialRecord = <T>(value: T): Record<ApiGranularity, T> =>
  API_KEYS.reduce(
    (record, key) => ({ ...record, [key]: value }),
    {} as Record<ApiGranularity, T>,
  );

const initialState: PrintOverviewState = {
  pointsByKey: initialRecord<PrintOverviewPoint[] | null>(null),
  statusByKey: initialRecord<LoadingStatus>("idle"),
  errorByKey: initialRecord<ApiError | null>(null),
};

const printOverviewSlice = createSlice({
  name: "printOverview",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPrintOverview.pending, (state, action) => {
        const key = API_GRANULARITY[action.meta.arg];
        state.statusByKey[key] = "loading";
        state.errorByKey[key] = null;
      })
      .addCase(fetchPrintOverview.fulfilled, (state, action) => {
        // Replaces ONLY this level — the other granularities' caches stay
        // intact for the chart still displaying them.
        const key = API_GRANULARITY[action.meta.arg];
        state.pointsByKey[key] = action.payload;
        state.statusByKey[key] = "success";
      })
      .addCase(fetchPrintOverview.rejected, (state, action) => {
        // Aborted requests are superseded fetches, not failures (section 49).
        if (action.meta.aborted) return;
        const key = API_GRANULARITY[action.meta.arg];
        state.statusByKey[key] = "error";
        state.errorByKey[key] = { code: "THUNK_ERROR", message: action.error.message ?? "Unknown error" };
      });
  },
});

export const printOverviewReducer = printOverviewSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors — granularity-scoped: a chart passes its own drill level and
// reads only that level's cache (`week` reads the day cache it derives from)
// ---------------------------------------------------------------------------

function cacheKey(granularity: OverviewGranularity): ApiGranularity {
  return API_GRANULARITY[granularity];
}

export function selectPrintOverviewPoints(
  state: RootState,
  granularity: OverviewGranularity,
): PrintOverviewPoint[] {
  return state.printOverview.pointsByKey[cacheKey(granularity)] ?? [];
}

export function selectPrintOverviewLoading(
  state: RootState,
  granularity: OverviewGranularity,
): boolean {
  return state.printOverview.statusByKey[cacheKey(granularity)] === "loading";
}

export function selectPrintOverviewError(
  state: RootState,
  granularity: OverviewGranularity,
): ApiError | null {
  return state.printOverview.errorByKey[cacheKey(granularity)];
}

/**
 * The KPI base: the DAY cache, always — a day row exists only when that day
 * ran, so `computeKpi` counts the rows as the ACTIVE-DAYS denominator for
 * the per-day averages (backend-exact; no separate "count days" API). The
 * KPI cards dispatch a day-level fetch on mount, independent of the
 * charts' drill levels.
 */
export function selectPrintOverviewKpiBase(state: RootState): PrintOverviewPoint[] {
  return state.printOverview.pointsByKey.day ?? [];
}
